import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiCustomFieldValue } from "@/types/custom-field.types.ts";
import {
  ApiGoalConfidenceLevel,
  ApiGoalKeyResult,
  ApiGoalMetricType,
  ApiGoalOwnerType,
  ApiGoalShort,
  ApiGoalStatus,
} from "@/types/goals.types.ts";
import {
  Avatar,
  FontAwesomeIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { endOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";

export enum GoalStatusColor {
  full_green = "full_green",
  green = "green",
  gray = "gray",
  red = "red",
  orange = "orange",
}

export interface GoalCompositeStatus {
  label: string;
  color: GoalStatusColor;
}

export default function useGoalsHook() {
  const { t } = useTranslation();
  const { renderCurrency, renderEmployeeName, renderShortPeriod, renderMonthYear } = useFormatter();

  const renderKeyResultValue = (
    value: ApiCustomFieldValue | null | undefined,
    metricType: ApiGoalMetricType
  ): string => {
    if (!value) return "";

    switch (metricType) {
      case ApiGoalMetricType.number:
        return value.value_float != null ? Number(value.value_float).toString() : "";
      case ApiGoalMetricType.boolean:
        return value.value_bool ? t("common:yes") : t("common:no");
      case ApiGoalMetricType.percentage:
        return value.value_float != null ? `${Number(value.value_float)}%` : "";
      case ApiGoalMetricType.currency:
        return value.value_float != null
          ? renderCurrency(Number(value.value_float).toString(), value.value_text ?? null)
          : "";
      default:
        return "";
    }
  };

  const renderKeyResultProgress = (keyResult: ApiGoalKeyResult) => {
    const start = renderKeyResultValue(keyResult.start_value, keyResult.metric_type);
    const target = renderKeyResultValue(keyResult.target_value, keyResult.metric_type);

    const startEl = (
      <Tooltip>
        <TooltipTrigger>
          <span className="text-secondary cursor-default">{start}</span>
        </TooltipTrigger>
        <TooltipContent>{t("goals:start_value")}</TooltipContent>
      </Tooltip>
    );

    const targetEl = (
      <span className="text-secondary cursor-default">
        →&nbsp;
        <Tooltip>
          <TooltipTrigger>{target}</TooltipTrigger>
          <TooltipContent>{t("goals:target_value")}</TooltipContent>
        </Tooltip>
      </span>
    );

    if (keyResult.current_value) {
      const current = renderKeyResultValue(keyResult.current_value, keyResult.metric_type);
      const currentEl = (
        <span className="text-secondary cursor-default">
          <Tooltip>
            <TooltipTrigger>
              <span className={"text-dark/95"}>{current}</span>
            </TooltipTrigger>
            <TooltipContent>{t("goals:current_value")}</TooltipContent>
          </Tooltip>
        </span>
      );

      return currentEl;
    }

    return (
      <>
        {startEl} {targetEl}
      </>
    );
  };

  const renderOwnerAvatar = (goal?: ApiGoalShort) => {
    if (!goal) return null;

    switch (goal.owner_type) {
      case ApiGoalOwnerType.employee:
        return goal.owner_employee ? <EmployeeAvatar employee={goal.owner_employee} /> : null;
      case ApiGoalOwnerType.department:
        return goal.owner_department ? (
          <Avatar
            acronym={goal.owner_department.title}
            url={goal.owner_department.image_url}
            emoji={goal.owner_department.emoji}
          />
        ) : null;
      case ApiGoalOwnerType.team:
        return goal.owner_team ? (
          <Avatar acronym={goal.owner_team.title} emoji={goal.owner_team.emoji} />
        ) : null;
      case ApiGoalOwnerType.company:
        return goal.owner_company ? (
          <Avatar acronym={goal.owner_company.title} url={goal.owner_company.logo_url} />
        ) : null;
      default:
        return null;
    }
  };

  const getGoalOwnerSubAvatar = (goal: ApiGoalShort) => {
    switch (goal.owner_type) {
      case ApiGoalOwnerType.department:
        return goal.owner_department
          ? {
              acronym: goal.owner_department.title,
              url: goal.owner_department.image_url,
              emoji: goal.owner_department.emoji,
            }
          : undefined;
      case ApiGoalOwnerType.team:
        return goal.owner_team
          ? {
              acronym: goal.owner_team.title,
              emoji: goal.owner_team.emoji,
            }
          : undefined;
      case ApiGoalOwnerType.company:
        return goal.owner_company
          ? {
              acronym: goal.owner_company.title,
              url: goal.owner_company.logo_url,
            }
          : undefined;
      default:
        return undefined;
    }
  };

  const renderAssignToAvatar = (goal?: ApiGoalShort) => {
    if (!goal) return null;

    switch (goal.owner_type) {
      case ApiGoalOwnerType.employee:
        return goal.assign_to ? <EmployeeAvatar employee={goal.assign_to} /> : null;
      case ApiGoalOwnerType.department:
        return goal.assign_to ? (
          <EmployeeAvatar employee={goal.assign_to} subAvatar={getGoalOwnerSubAvatar(goal)} />
        ) : goal.owner_department ? (
          <Avatar
            acronym={goal.owner_department.title}
            url={goal.owner_department.image_url}
            emoji={goal.owner_department.emoji}
          />
        ) : null;

      case ApiGoalOwnerType.team:
        return goal.assign_to ? (
          <EmployeeAvatar employee={goal.assign_to} subAvatar={getGoalOwnerSubAvatar(goal)} />
        ) : goal.owner_team ? (
          <Avatar acronym={goal.owner_team.title} emoji={goal.owner_team.emoji} />
        ) : null;

      case ApiGoalOwnerType.company:
        return goal.assign_to ? (
          <EmployeeAvatar employee={goal.assign_to} subAvatar={getGoalOwnerSubAvatar(goal)} />
        ) : goal.owner_company ? (
          <Avatar acronym={goal.owner_company.title} url={goal.owner_company.logo_url} />
        ) : null;
      default:
        return null;
    }
  };

  const getOwnerTitle = (goal?: ApiGoalShort) => {
    if (!goal) return "";

    switch (goal.owner_type) {
      case ApiGoalOwnerType.employee: {
        const employee = goal.owner_employee;
        return employee ? renderEmployeeName(employee) : "";
      }
      case ApiGoalOwnerType.department:
        return goal.owner_department?.title ?? "";
      case ApiGoalOwnerType.team:
        return goal.owner_team?.title ?? "";
      case ApiGoalOwnerType.company:
        return goal.owner_company?.title ?? "";
      default:
        return "";
    }
  };

  const getOwnerName = (goal?: ApiGoalShort): string | null => {
    if (!goal) return null;

    switch (goal.owner_type) {
      case ApiGoalOwnerType.department:
        return goal.owner_department?.title ?? null;
      case ApiGoalOwnerType.team:
        return goal.owner_team?.title ?? null;
      case ApiGoalOwnerType.company:
        return goal.owner_company?.title ?? null;
      default:
        return null;
    }
  };

  const getOwnerTypeTitle = (ownerType: ApiGoalOwnerType): string => {
    switch (ownerType) {
      case ApiGoalOwnerType.company:
        return t("goals:company_goal");
      case ApiGoalOwnerType.department:
        return t("goals:department_goal");
      case ApiGoalOwnerType.team:
        return t("goals:team_goal");
      case ApiGoalOwnerType.employee:
        return t("goals:employee_goal");
    }
  };

  const getGoalStatus = (goal: ApiGoalShort): GoalCompositeStatus => {
    switch (goal.status) {
      case ApiGoalStatus.active:
        return { label: t("goals:status_active"), color: GoalStatusColor.green };
      case ApiGoalStatus.cancelled:
        return { label: t("goals:status_cancelled"), color: GoalStatusColor.gray };
      case ApiGoalStatus.completed:
        return { label: t("goals:status_completed"), color: GoalStatusColor.full_green };
      case ApiGoalStatus.failed:
        return { label: t("goals:status_failed"), color: GoalStatusColor.red };
    }
  };

  const getGoalConfidenceLevel = (goal: ApiGoalShort): GoalCompositeStatus => {
    switch (goal.confidence_level) {
      case ApiGoalConfidenceLevel.at_risk:
        return { label: t("goals:at_risk"), color: GoalStatusColor.red };
      case ApiGoalConfidenceLevel.on_track:
        return { label: t("goals:on_track"), color: GoalStatusColor.green };
      case ApiGoalConfidenceLevel.off_track:
        return { label: t("goals:off_track"), color: GoalStatusColor.orange };
    }
  };

  const renderPeriod = (dateStart: Date, dateEnd: Date): string => {
    const start = new Date(dateStart);
    const end = new Date(dateEnd);

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();
    const startDay = start.getDate();
    const endDay = end.getDate();

    if (
      startYear === endYear &&
      startMonth === 0 &&
      endMonth === 11 &&
      startDay === 1 &&
      endDay === 31
    ) {
      return `${startYear}`;
    }

    const quarterStartMonths = [0, 3, 6, 9];
    if (
      startYear === endYear &&
      quarterStartMonths.includes(startMonth) &&
      endMonth === startMonth + 2 &&
      startDay === 1 &&
      endDay === endOfMonth(end).getDate()
    ) {
      const quarter = Math.floor(startMonth / 3) + 1;
      return t("goals:quarter_format", { quarter, year: startYear });
    }

    if (
      startYear === endYear &&
      startMonth === endMonth &&
      startDay === 1 &&
      endDay === endOfMonth(end).getDate()
    ) {
      return renderMonthYear(start);
    }

    return renderShortPeriod(start, end);
  };

  const getKeyResultIcon = (metricType: ApiGoalMetricType) => {
    switch (metricType) {
      case ApiGoalMetricType.boolean:
        return <FontAwesomeIcon icon="fa-light fa-bullseye" />;
      case ApiGoalMetricType.percentage:
        return <FontAwesomeIcon icon="fa-light fa-percent" />;
      case ApiGoalMetricType.currency:
        return <FontAwesomeIcon icon="fa-light fa-coins" />;
      case ApiGoalMetricType.number:
        return <FontAwesomeIcon icon="fa-light fa-chart-line" />;
    }
  };

  return {
    renderKeyResultValue,
    renderKeyResultProgress,
    renderOwnerAvatar,
    renderAssignToAvatar,
    getOwnerTitle,
    getOwnerName,
    getOwnerTypeTitle,
    getGoalStatus,
    getGoalConfidenceLevel,
    renderPeriod,
    getKeyResultIcon,
  };
}
